import { browserHistory } from 'common/routing/browser_history';
import { keys, replace, split } from 'lodash';
import { useEffect } from 'react';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

export const hashValuePattern = '(\\#|\\&)({key})\\=[^&]+(?=\\&)?';

export type KeyValues = { [key: string]: string };

export const getHashValue = (key: string): string => {
    const search: string = browserHistory.location?.hash;
    const keyValueRegex = new RegExp(replace(hashValuePattern, '{key}', key));
    const matches = search.match(keyValueRegex);
    if (!matches?.length) {
        return null;
    }
    const keyValue = split(matches[0], '=');

    return decodeURIComponent(keyValue[keyValue.length - 1]);
};

export const getQueryValue = (key: string): string => {
    const search = new URLSearchParams(browserHistory.location?.search);
    const value = search.get(key);

    return value ? decodeURIComponent(value) : null;
};

export const getQueryValues = (keys: string[]): KeyValues => {
    const keyValues: KeyValues = {};
    for (const key of keys || []) {
        const value = getQueryValue(key);
        keyValues[key] = value;
    }

    return keyValues;
};

export const setQueryValue = (key: string, value: string): void => {
    const search = new URLSearchParams(browserHistory.location?.search);
    const encodedValue = value ? encodeURIComponent(value) : '';
    if (search.get(key)) {
        search.set(key, encodedValue);
    } else {
        search.append(key, encodedValue);
    }

    const s = search.toString();
    // eslint-disable-next-line lodash/prefer-lodash-method
    browserHistory.replace(s[0] === '?' ? s : `?${s}`);
};

export const setQueryValues = (keyValues: KeyValues): void => {
    const search = new URLSearchParams(browserHistory.location?.search);
    for (const key of keys(keyValues) || []) {
        const value = keyValues[key];
        const encodedValue = value ? encodeURIComponent(value) : '';
        if (search.get(key)) {
            search.set(key, encodedValue);
        } else {
            search.append(key, encodedValue);
        }
    }

    const s = search.toString();
    browserHistory.push(s[0] === '?' ? s : `?${s}`);
};

export const useListenToQueryValues = (keys: string[], deps?: any[]): Observable<KeyValues> => {
    const queryValues = new Subject<KeyValues>();
    const unsubscribe = new Subject<void>();
    useEffect(() => {
        const unlisten = browserHistory.listen(() => {
            queryValues.next(getQueryValues(keys));
        });
        queryValues.next(getQueryValues(keys));

        return (): void => {
            unlisten();
            unsubscribe.next();
            unsubscribe.complete();
        };
    }, [...(deps || [])]);

    return queryValues.asObservable().pipe(debounceTime(100), takeUntil(unsubscribe));
};
